import React, { useRef, useState, useEffect } from 'react';
import { X, Eraser } from 'lucide-react';

/**
 * Lightweight canvas signature pad modal (no external dependency).
 *
 * onSign receives either { signatureImage: <base64 png> } when the user draws,
 * or { useStored: true } when they choose to reuse their saved signature.
 */
const SignaturePadModal = ({
  title = 'Sign as employer',
  onClose,
  onSign,
  allowStored = true,
  // Stacking layer. The default suits a pad opened from an ordinary page, but a
  // caller that is itself a full-screen overlay must hand down something above
  // its own — otherwise this modal mounts BEHIND it and the button that opened
  // it looks broken. See MandatoryDocGate, which sits at z-[9999].
  overlayZClass = 'z-50',
}) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Scale the backing store to the displayed size for crisp lines.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawn) setHasDrawn(true);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const submitDrawn = async () => {
    if (!hasDrawn || submitting) return;
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      await onSign({ signatureImage: dataUrl });
    } finally {
      setSubmitting(false);
    }
  };

  const submitStored = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSign({ useStored: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center p-4 ${overlayZClass}`}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-wide">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          <p className="text-xs text-slate-500 mb-2">Draw your signature in the box below.</p>
          <div className="relative border-2 border-dashed border-slate-200 rounded-xl bg-white">
            <canvas
              ref={canvasRef}
              className="w-full h-40 touch-none rounded-xl"
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
            />
            <button
              onClick={clear}
              type="button"
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              title="Clear"
            >
              <Eraser className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Drawn e-signature with audit trail (IP, time, document hash) — not a qualified electronic signature.
          </p>
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-2">
          {allowStored ? (
            <button
              onClick={submitStored}
              disabled={submitting}
              className="text-sm font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-50"
            >
              Use saved signature
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submitDrawn}
              disabled={!hasDrawn || submitting}
              className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Signing…' : 'Sign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturePadModal;
